import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import RoasterSelector from "../components/RoasterSelector";
import { apiFetch } from "../api";
import "./SessionConfirmBatches.css";

const SessionConfirmBatches = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [identifiedBatches, setIdentifiedBatches] = useState([]);
  const [uncertainBatches, setUncertainBatches] = useState([]);
  const [batchConfirmations, setBatchConfirmations] = useState({});
  const [newBatchInput, setNewBatchInput] = useState("");
  const [newRoasterInput, setNewRoasterInput] = useState("");
  const [agentNotes, setAgentNotes] = useState("");
  const [userRoasters, setUserRoasters] = useState([]);

  useEffect(() => {
    fetchSession();
    fetchUserRoasters();
  }, [sessionId]);

  const fetchUserRoasters = async () => {
    try {
      const response = await apiFetch("/roasters");
      if (response.ok) {
        const roasters = await response.json();
        setUserRoasters(roasters);
      }
    } catch (err) {
      console.error("Failed to fetch roasters:", err);
    }
  };

  const fetchSession = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await apiFetch(`/sessions/${sessionId}`);

      if (!response.ok) {
        throw new Error("Failed to fetch session");
      }

      const session = await response.json();

      if (session.status !== "identified") {
        navigate(`/sessions`);
        return;
      }

      const identified = session.identified_batches || {};
      const confident = identified.batches || [];
      const uncertain = identified.uncertain || [];
      const notes = identified.notes || "";

      setIdentifiedBatches(confident);
      setUncertainBatches(uncertain);

      // Initialize batch confirmations with roaster name strings
      const confirmations = {};
      confident.forEach((batch) => {
        const key = `${batch.roaster_name || "none"}-${batch.batch_label}`;
        confirmations[key] = {
          roaster_name: batch.roaster_name || "",
          batch_label: batch.batch_label,
          roaster_id: batch.roaster_match?.roaster_id || null,
        };
      });
      setBatchConfirmations(confirmations);
      setAgentNotes(notes);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleRemoveBatch = (batch) => {
    const key = `${batch.roaster_name || "none"}-${batch.batch_label}`;
    setIdentifiedBatches((prev) =>
      prev.filter(
        (b) => `${b.roaster_name || "none"}-${b.batch_label}` !== key,
      ),
    );
    setUncertainBatches((prev) =>
      prev.filter(
        (b) => `${b.roaster_name || "none"}-${b.batch_label}` !== key,
      ),
    );
    setBatchConfirmations((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const handleAddBatch = () => {
    const trimmedBatch = newBatchInput.trim();
    const trimmedRoaster = newRoasterInput.trim() || "";

    if (!trimmedBatch) {
      return;
    }

    const key = `${trimmedRoaster || "none"}-${trimmedBatch}`;

    // Check for duplicates
    if (batchConfirmations[key]) {
      setError("This batch label is already in the list");
      return;
    }

    // Find matching roaster ID if name matches existing roaster
    const matchingRoaster = userRoasters.find(
      (r) => r.name.toLowerCase() === trimmedRoaster.toLowerCase(),
    );

    const newBatch = {
      roaster_name: trimmedRoaster,
      batch_label: trimmedBatch,
      roaster_match: {
        roaster_id: matchingRoaster?.id || null,
        roaster_name: trimmedRoaster,
        is_new: !matchingRoaster,
        suggestions: [],
      },
    };

    setIdentifiedBatches((prev) => [...prev, newBatch]);
    setBatchConfirmations((prev) => ({
      ...prev,
      [key]: {
        roaster_name: trimmedRoaster,
        batch_label: trimmedBatch,
        roaster_id: matchingRoaster?.id || null,
      },
    }));
    setNewBatchInput("");
    setNewRoasterInput("");
    setError("");
  };

  const handleRoasterChange = (batchKey, newName) => {
    const trimmedName = newName.trim();

    // Find matching roaster ID if name matches existing roaster (case-insensitive)
    const matchingRoaster = trimmedName
      ? userRoasters.find(
          (r) => r.name.toLowerCase() === trimmedName.toLowerCase(),
        )
      : null;

    setBatchConfirmations((prev) => ({
      ...prev,
      [batchKey]: {
        ...prev[batchKey],
        roaster_name: trimmedName,
        roaster_id: matchingRoaster?.id || null,
      },
    }));
  };

  const handleConfirm = async () => {
    if (Object.keys(batchConfirmations).length === 0) {
      setError("Please confirm at least one batch to continue");
      return;
    }

    setProcessing(true);
    setError("");

    try {
      // Step 1: Identify unique new roaster names (those without roaster_id)
      const newRoasterNames = new Set();
      Object.values(batchConfirmations).forEach((conf) => {
        const trimmedName = conf.roaster_name?.trim();
        if (trimmedName && !conf.roaster_id) {
          newRoasterNames.add(trimmedName);
        }
      });

      // Step 2: Create all new roasters and build a name->id map
      const newRoastersMap = {};
      for (const roasterName of newRoasterNames) {
        try {
          const createResponse = await apiFetch("/roasters", {
            method: "POST",
            body: JSON.stringify({ name: roasterName }),
          });

          if (createResponse.ok) {
            const newRoaster = await createResponse.json();
            newRoastersMap[roasterName.toLowerCase()] = newRoaster.id;
          } else {
            const errorData = await createResponse.json();
            throw new Error(
              `Failed to create roaster "${roasterName}": ${errorData.detail}`,
            );
          }
        } catch (err) {
          throw new Error(
            `Failed to create roaster "${roasterName}": ${err.message}`,
          );
        }
      }

      // Step 3: Create/get all batch records
      const batchCreationPromises = Object.values(batchConfirmations).map(
        async (conf) => {
          // Resolve roaster_id: use existing ID, new ID from map, or null
          let resolvedRoasterId = conf.roaster_id;
          if (!resolvedRoasterId && conf.roaster_name?.trim()) {
            const normalizedName = conf.roaster_name.trim().toLowerCase();
            resolvedRoasterId = newRoastersMap[normalizedName] || null;
          }

          // Check if batch exists
          const searchResponse = await apiFetch("/batches");
          if (searchResponse.ok) {
            const existingBatches = await searchResponse.json();
            const existing = existingBatches.find(
              (b) =>
                b.batch_label === conf.batch_label &&
                b.roaster_id === resolvedRoasterId,
            );
            if (existing) {
              return existing.id;
            }
          }

          // Create new batch
          const createResponse = await apiFetch("/batches", {
            method: "POST",
            body: JSON.stringify({
              batch_label: conf.batch_label,
              roaster_id: resolvedRoasterId,
            }),
          });

          if (createResponse.ok) {
            const newBatch = await createResponse.json();
            return newBatch.id;
          }
          throw new Error(`Failed to create batch: ${conf.batch_label}`);
        },
      );

      const batchIds = await Promise.all(batchCreationPromises);

      // Step 4: Confirm batches with batch IDs (sets status to 'processing')
      const confirmResponse = await apiFetch(
        `/sessions/${sessionId}/confirm-batches`,
        {
          method: "PUT",
          body: JSON.stringify({ confirmed_batches: batchIds }),
        },
      );

      if (!confirmResponse.ok) {
        const errorData = await confirmResponse.json();
        throw new Error(errorData.detail || "Failed to confirm batches");
      }

      // Step 5: Trigger aggregation (Agent 2 processes and sets status to 'complete')
      const aggregateResponse = await apiFetch(
        `/sessions/${sessionId}/aggregate`,
        {
          method: "POST",
        },
      );

      if (!aggregateResponse.ok) {
        const errorData = await aggregateResponse.json();
        throw new Error(errorData.detail || "Failed to aggregate notes");
      }

      // Step 6: Navigate to results page
      navigate(`/sessions/${sessionId}/results`);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    navigate("/sessions");
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">Loading session...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Header user={user} onLogout={handleLogout} />

      <main className="dashboard-content confirm-content">
        <div className="confirm-header">
          <h2>Review Identified Batches</h2>
          <p className="confirm-subtitle">
            Review and confirm which batches were cupped in this session
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="batches-section">
          <h3>Identified Batches</h3>

          {identifiedBatches.length === 0 && uncertainBatches.length === 0 ? (
            <p className="no-batches-message">
              No batch labels found. Please add them manually below.
            </p>
          ) : (
            <div className="batch-list">
              {identifiedBatches.map((batch) => {
                const key = `${batch.roaster_name || "none"}-${batch.batch_label}`;
                return (
                  <div key={key} className="batch-item-row">
                    <div className="batch-item-content">
                      <div className="batch-roaster">
                        <label>Roaster:</label>
                        <RoasterSelector
                          roasterName={
                            batchConfirmations[key]?.roaster_name || ""
                          }
                          onRoasterChange={(newName) =>
                            handleRoasterChange(key, newName)
                          }
                          suggestions={userRoasters}
                        />
                      </div>
                      <div className="batch-label-section">
                        <label>Batch:</label>
                        <span className="batch-label">{batch.batch_label}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveBatch(batch)}
                      className="btn-remove"
                      aria-label="Remove batch"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}

              {uncertainBatches.map((batch) => {
                const key = `${batch.roaster_name || "none"}-${batch.batch_label}`;
                return (
                  <div key={key} className="batch-item-row uncertain">
                    <div className="batch-item-content">
                      <span className="warning-icon">⚠</span>
                      <div className="batch-roaster">
                        <label>Roaster:</label>
                        <RoasterSelector
                          roasterName={
                            batchConfirmations[key]?.roaster_name || ""
                          }
                          onRoasterChange={(newName) =>
                            handleRoasterChange(key, newName)
                          }
                          suggestions={userRoasters}
                        />
                      </div>
                      <div className="batch-label-section">
                        <label>Batch:</label>
                        <span className="batch-label">{batch.batch_label}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveBatch(batch)}
                      className="btn-remove"
                      aria-label="Remove batch"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="add-batch-section">
          <h3>Missing a batch? Add it manually:</h3>
          <div className="add-batch-controls">
            <input
              type="text"
              value={newRoasterInput}
              onChange={(e) => setNewRoasterInput(e.target.value)}
              placeholder="Roaster (optional)"
              className="roaster-input-field"
              maxLength={255}
            />
            <input
              type="text"
              value={newBatchInput}
              onChange={(e) => setNewBatchInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleAddBatch()}
              placeholder="Batch label (e.g., GB-3)"
              className="batch-input"
              maxLength={100}
            />
            <button onClick={handleAddBatch} className="btn-add">
              + Add
            </button>
          </div>
        </div>

        {agentNotes && (
          <div className="agent-notes">
            <h3>Notes from Agent:</h3>
            <p>{agentNotes}</p>
          </div>
        )}

        <div className="confirm-actions">
          <button onClick={handleCancel} className="btn-cancel">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={
              Object.keys(batchConfirmations).length === 0 || processing
            }
            className="btn-confirm"
          >
            {processing ? "Processing & Aggregating..." : "Confirm & Process"}
          </button>
        </div>
      </main>
    </div>
  );
};

export default SessionConfirmBatches;
